import { observeMediaElement } from "../../utils/mediaTelemetry";
import { whenConsented, grantConsent } from "../../utils/consent";

// tarteaucitron service key, see src/php/Consent.php.
const YOUTUBE_CONSENT = "vsyoutube";

export default class VideoPlayer {
  constructor(el) {
    this.el = el;
    this.hideTimer = null;
    this.init();
  }

  async init() {
    await Promise.all([
      import('vidstack/player'),
      import('vidstack/player/ui'),
      import('vidstack/player/layouts/default'),
      import('vidstack/icons'),
      import('vidstack/player/styles/base.css'),
      import('vidstack/player/styles/default/theme.css'),
      import('vidstack/player/styles/default/layouts/video.css'),
    ]);

    this.player = this.el.querySelector('media-player');
    this.wasPlaying = false;

    const consentSrc = this.player.dataset.consentSrc;
    if (consentSrc) this.initConsent(consentSrc);

    observeMediaElement(this.player, {
      context: "video-player",
      kind: "embed",
      src: this.player.getAttribute("src") ?? consentSrc,
    });

    this.player.subscribe(({ paused, ended }) => {
      const isPlaying = !paused && !ended;
      this.el.classList.toggle('is-playing', isPlaying);

      if (isPlaying === this.wasPlaying) return;
      this.wasPlaying = isPlaying;

      if (isPlaying) {
        this.scheduleHide();
      } else {
        clearTimeout(this.hideTimer);
        this.el.classList.remove('is-btn-hidden');
      }
    });

    this.el.addEventListener('mousemove', () => this.onMouseMove());
  }

  // YouTube embed: the player has no src (so loads nothing from YouTube) until
  // the "vsyoutube" consent is given, from the overlay's button or from the
  // cookie banner / preferences panel.
  initConsent(src) {
    let playOnConsent = false;

    whenConsented(YOUTUBE_CONSENT, () => {
      this.el.classList.remove('is-consent-pending');
      this.player.src = src;
      // The click on "Accept and play" was a play request too. A play() sent
      // while Vidstack is still setting up the new YouTube provider is dropped,
      // so load it now and play once it's ready. The browser may still refuse
      // an unmuted start this late; the play button is back anyway.
      if (playOnConsent) {
        this.player.addEventListener('can-play', () => this.player.play().catch(() => {}), { once: true });
        this.player.startLoading();
      }
    });

    this.el.querySelector('.c-video-player__consent-accept')?.addEventListener('click', () => {
      playOnConsent = true;
      grantConsent(YOUTUBE_CONSENT);
    });
  }

  onMouseMove() {
    this.el.classList.remove('is-btn-hidden');

    if (this.el.classList.contains('is-playing')) {
      this.scheduleHide();
    } else {
      clearTimeout(this.hideTimer);
    }
  }

  scheduleHide() {
    clearTimeout(this.hideTimer);
    this.hideTimer = setTimeout(() => {
      if (this.el.classList.contains('is-playing')) {
        this.el.classList.add('is-btn-hidden');
      }
    }, 1000);
  }
}
